<script>
  import { onMount } from "svelte"
  // El registro automático evita referencias incompletas con el Rollup de este proyecto.
  import Chart from "chart.js/auto"
  import { navigate } from "svelte-routing"
  import { user } from "../../stores/User"
  import { BASE_URL } from "../../stores/BASE_URL"
  import getMoneda from "../../services/moneda.js"
  import Spinner from "../Spinner.svelte"
  import SinInformacion from "../Sin_informacion.svelte"

  export let ruc

  const estados = ["verde", "gris", "amarillo", "rojo"]
  const colores = {
    verde: "#16a34a",
    gris: "#adb5bd",
    amarillo: "#ffbf39",
    rojo: "#dc2626",
  }
  const descripciones = {
    verde: "Mínimo riesgo: sin deudas vencidas",
    gris: "No registra información de deudas",
    amarillo: "Mediano riesgo: deudas con poco atraso",
    rojo: "Alto riesgo: deudas con atraso significativo",
  }

  let deudas = []
  let loading = true
  let error = ""

  onMount(() => {
    const controller = new AbortController()

    const cargarDatos = async () => {
      try {
        const params = new URLSearchParams({
          ops: "deudas_grafico",
          ruc,
          session_key: $user.session_key,
        })
        const response = await fetch(`${BASE_URL}empresa.php?${params}`, {
          signal: controller.signal,
        })
        if (!response.ok) throw new Error("No se pudo cargar el gráfico de deudas.")
        const resp = await response.json()
        if (resp.status === "error") {
          navigate("/", { replace: true })
          return
        }
        if (!Array.isArray(resp.data)) throw new Error("La respuesta del gráfico no es válida.")
        deudas = resp.data
      } catch (err) {
        if (err.name !== "AbortError") error = err.message
      } finally {
        if (!controller.signal.aborted) loading = false
      }
    }

    cargarDatos()
    return () => controller.abort()
  })

  function crearGrafico(canvas) {
    const estiloPunto = {
      radius: 13,
      borderColor: "#fff",
      borderWidth: 3,
      pointStyle: "circle",
    }
    const chart = new Chart(canvas, {
      type: "line",
      plugins: [{
        id: "circulosEstados",
        afterDatasetsDraw(chart) {
          const eje = chart.scales.y
          const Punto = Chart.registry.getElement("point")
          estados.forEach((estado) => {
            const punto = new Punto({
              x: eje.left + eje.width / 2,
              y: eje.getPixelForValue(estado),
              options: { ...estiloPunto, backgroundColor: colores[estado] },
            })
            punto.draw(chart.ctx)
          })
        },
      }],
      data: {
        labels: deudas.map((deuda) => deuda.mes),
        datasets: [{
          label: "Estado de deudas",
          // Las alturas son categorías, no cantidades ni una escala de riesgo.
          data: deudas.map((deuda) => estados.includes(deuda.semaforo) ? deuda.semaforo : null),
          borderColor: "#adb5bd",
          borderDash: [6, 4],
          borderWidth: 2,
          tension: 0,
          fill: false,
          spanGaps: false,
          pointRadius: estiloPunto.radius,
          pointHoverRadius: 15,
          pointHitRadius: 8,
          pointBackgroundColor: deudas.map((deuda) => colores[deuda.semaforo] || "#adb5bd"),
          pointBorderColor: estiloPunto.borderColor,
          pointBorderWidth: estiloPunto.borderWidth,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        layout: { padding: { top: 8, right: 16 } },
        interaction: { mode: "nearest", intersect: true },
        plugins: {
          legend: { display: false },
          tooltip: {
            displayColors: false,
            callbacks: {
              label: ({ dataIndex }) => {
                const deuda = deudas[dataIndex]
                return [
                  descripciones[deuda.semaforo] || "Estado desconocido",
                  `Monto total: ${getMoneda(deuda.monto)}`,
                ]
              },
            },
          },
        },
        scales: {
          x: {
            type: "category",
            offset: true,
            grid: { color: "#e9ecef", drawTicks: false },
            border: { color: "#e9ecef", width: 2, dash: [2, 3] },
            ticks: {
              autoSkip: false,
              minRotation: 55,
              maxRotation: 55,
              color: "#6c757d",
              padding: 12,
            },
          },
          y: {
            type: "category",
            labels: estados,
            offset: true,
            afterFit: (eje) => { eje.width = 56 },
            grid: { color: "#e9ecef", drawTicks: false },
            border: { color: "#e9ecef", width: 2, dash: [2, 3] },
            ticks: {
              autoSkip: false,
              display: false,
            },
          },
        },
      },
    })

    return { destroy: () => chart.destroy() }
  }
</script>

{#if loading}
  <Spinner />
{:else if error}
  <p class="alert theme-danger mb-0" role="alert">{error}</p>
{:else if deudas.length === 0}
  <SinInformacion />
{:else}
  <div class="chart-scroll">
    <div class="chart-container" style="min-width: {Math.max(480, deudas.length * 52 + 64)}px">
      <canvas use:crearGrafico aria-label="Historial mensual de estados de deudas de {ruc}">
        Historial de deudas por mes. Los datos se encuentran en la tabla siguiente.
      </canvas>
    </div>
  </div>
  <table class="visually-hidden">
    <caption>Historial de deudas de {ruc}</caption>
    <thead><tr><th scope="col">Mes</th><th scope="col">Estado</th><th scope="col">Monto total</th></tr></thead>
    <tbody>
      {#each deudas as deuda}
        <tr>
          <td>{deuda.mes}</td>
          <td>{descripciones[deuda.semaforo] || "Estado desconocido"}</td>
          <td>{getMoneda(deuda.monto)}</td>
        </tr>
      {/each}
    </tbody>
  </table>
{/if}

<style>
  .chart-scroll {
    overflow-x: auto;
  }

  .chart-container {
    position: relative;
    width: 100%;
    height: 300px;
  }
</style>
